import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { cn } from '@wwe-portal/ui/lib/lib';
import { ComponentProps, FC } from 'react';
import { TalentBadge } from './power-badge';
import { RosterTier } from './roster-tier';
import { RosterStar, RosterStarData } from '../roster-star/roaster-star';
import { AspectRatio } from '@wwe-portal/ui/components/aspect-ratio';
import { Skeleton, TextSkeleton } from '@wwe-portal/ui/components/skeleton';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

export interface RosterCardData {
  id?: string;
  title: string;
  talent?: number;
  image?: string;
  disabled?: boolean;
  tier?: string;
  star?: RosterStarData;
  notOwned?: boolean;
  fullName?: string;
  winPercentage?: number;
  totalUpgrades?: number;
}

interface RosterCardV2Props {
  data: RosterCardData;
  imageElement?: React.ReactNode;
  starElement?: React.ReactNode;
  footerElement?: React.ReactNode;
  onClick?: () => void;
}

export const RosterCardV2Layout: FC<
  {
    rosterImageElement?: React.ReactNode;
    footerElement?: React.ReactNode;
    tierElement?: React.ReactNode;
    otherElements?: React.ReactNode;
  } & ComponentProps<typeof ClickableDiv>
> = ({
  rosterImageElement,
  footerElement,
  tierElement,
  otherElements,
  className,
  ...props
}) => {
  return (
    <ClickableDiv
      className={cn(
        'font-poppins bg-back300 rounded-10 flex w-full flex-col items-center justify-center px-2.5 py-3',
        className
      )}
      {...props}
    >
      <div className="relative w-full">
        {rosterImageElement}
        {tierElement}
        {otherElements}
      </div>
      {footerElement}
    </ClickableDiv>
  );
};

export const RosterCardImage: FC<{
  imageElement: React.ReactNode;
  starElement: React.ReactNode;
}> = ({ imageElement, starElement }) => {
  return (
    <AspectRatio className="relative w-full" ratio={169 / 222}>
      {imageElement}
      <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center">
        {starElement}
      </div>
    </AspectRatio>
  );
};

export const RosterCardV2: FC<RosterCardV2Props> = ({
  data,
  imageElement,
  onClick,
  starElement,
  footerElement,
}) => {
  const { id, title, talent, image, disabled, tier, star, notOwned } = data;
  return (
    <RosterCardV2Layout
      className={cn({ 'pointer-events-none grayscale': disabled })}
      onClick={onClick}
      data-roster-id={id}
      rosterImageElement={
        <RosterCardImage
          imageElement={
            imageElement ?? (
              <img
                alt="player card v2"
                className="h-full w-full object-contain"
                src={image}
              />
            )
          }
          starElement={
            star?.level != null &&
            (starElement ?? (
              <RosterStar
                className="gap-1 drop-shadow-[1px_1px_2px_rgba(0,0,0,0.35)]"
                hollowStar={star.hollowStar}
                level={star.level}
                rate={star.rate}
                size={25}
              />
            ))
          }
        />
      }
      tierElement={
        tier && (
          <div className="absolute bottom-[3%] left-0 right-0 flex translate-y-full items-center justify-center">
            <div className="w-[150px]">
              <RosterTier tier={tier} size="long" />
            </div>
          </div>
        )
      }
      otherElements={
        <>
          {notOwned && (
            <div className="absolute bottom-2 left-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500">
              <IcoMoonIcon className="h-3 w-3 text-white" iconName="crown-2" />
            </div>
          )}
        </>
      }
      footerElement={
        footerElement ?? (
          <div className="mt-4 flex h-[126px] w-full flex-col items-center gap-3">
            <div className="line-clamp-1 w-full text-center text-sm font-medium uppercase leading-6 text-white">
              <em>{title}</em>
            </div>

            {talent != null && (
              <div className="h-8">
                <TalentBadge value={talent} direction="up" />
              </div>
            )}
          </div>
        )
      }
    />
  );
};

export const RosterCardV2Skeleton: FC<{
  hasFooter?: boolean;
  footerElement?: React.ReactNode;
}> = ({ hasFooter, footerElement }) => {
  return (
    <RosterCardV2Layout
      rosterImageElement={
        <RosterCardImage
          imageElement={<Skeleton className="h-full w-full" />}
          starElement={null}
        />
      }
      tierElement={undefined}
      footerElement={
        hasFooter
          ? (footerElement ?? (
              <div className="mt-4 flex h-[126px] w-full flex-col items-center gap-3">
                <div className="flex items-center justify-center text-white">
                  <TextSkeleton fontSize={14} lineHeight={24} />
                </div>
              </div>
            ))
          : undefined
      }
    />
  );
};
